import {
  Button,
  Flex,
  MediaQuery,
  Select,
  SimpleGrid,
  Textarea,
} from "@mantine/core";
import { useForm } from "@mantine/form";
import { useCallback } from "react";

import SectionTitle from "../../../components/Section/SectionTitle/SectionTitle.tsx";
import { yesNoOptions } from "../../../constants.ts";
import { UpdateCustomerRiskMonitoringRemarksInput } from "../../../graphql/generated.ts";
import {
  RiskMonitoringRemarks,
  riskRecommendationOptions,
} from "../../../types/customer.ts";
import { booleanToString, stringToBoolean } from "../../../utils/boolean.ts";
import { useStyles } from "./CompanyInfo.styles.ts";

interface Props {
  value: RiskMonitoringRemarks | null | undefined;
  onCancel: () => void;
  onSubmit: (value: UpdateCustomerRiskMonitoringRemarksInput) => void;
}

const RiskMonitoringRemarksForm = ({ value, onCancel, onSubmit }: Props) => {
  const { classes } = useStyles();

  const form = useForm({
    initialValues: {
      priceAdjustment: booleanToString(value?.priceAdjustment, "formValue"),
      riskRecommendation: value?.riskRecommendation,
      notes: value?.notes,
    },
    validate: {},
    validateInputOnBlur: true,
  });

  const handleCancel = useCallback(() => {
    form.reset();
    onCancel();
  }, [form, onCancel]);

  const handleSubmit = useCallback(() => {
    if (!form.isValid()) {
      return;
    }
    onSubmit({
      ...form.values,
      priceAdjustment: stringToBoolean(form.values.priceAdjustment, true),
    } as UpdateCustomerRiskMonitoringRemarksInput);
    handleCancel();
  }, [form, handleCancel, onSubmit]);

  return (
    <>
      <MediaQuery smallerThan="sm" styles={{ display: "none" }}>
        <Flex gap={20}>
          <Button
            variant="outlineGolden"
            type="reset"
            uppercase
            onClick={handleCancel}
          >
            cancel
          </Button>
          <Button
            variant="filledGolden"
            type="submit"
            uppercase
            disabled={!form.isValid()}
            onClick={handleSubmit}
          >
            save
          </Button>
        </Flex>
      </MediaQuery>
      <MediaQuery largerThan="sm" styles={{ display: "none" }}>
        <Flex gap={20} className={classes.buttonsWrapper}>
          <Button
            variant="outlineGolden"
            type="reset"
            uppercase
            w="100%"
            onClick={handleCancel}
          >
            cancel
          </Button>
          <Button
            variant="filledGolden"
            type="submit"
            uppercase
            w="100%"
            disabled={!form.isValid()}
            onClick={handleSubmit}
          >
            save
          </Button>
        </Flex>
      </MediaQuery>
      <SimpleGrid
        cols={3}
        breakpoints={[{ maxWidth: "sm", cols: 1 }]}
        spacing={20}
        verticalSpacing={16}
      >
        <Flex direction="column" gap={12}>
          <SectionTitle flex={0} isSmall>
            Risk Monitoring
          </SectionTitle>
          <Select
            data={yesNoOptions}
            size="m"
            label="Price Adjustment"
            placeholder="Select Price Adjustment"
            clearable
            {...form.getInputProps("priceAdjustment")}
          />
          <Select
            data={riskRecommendationOptions}
            size="m"
            label="Risk Recommendation"
            placeholder="Select Risk Recommendation"
            clearable
            {...form.getInputProps("riskRecommendation")}
          />
          <Textarea
            label="Remarks"
            placeholder="Enter Remarks"
            autosize
            size="m"
            minRows={3}
            {...form.getInputProps("notes")}
          />
        </Flex>
      </SimpleGrid>
    </>
  );
};

export default RiskMonitoringRemarksForm;
